import { useEffect, useState } from "react";

const DEFAULT_INTERVAL_MS = 30_000;

/**
 * A clock that re-renders on a fixed interval.
 *
 * Booking classification is time-dependent — an appointment slips from Booked
 * to Missed the moment its slot ends — and `Date.now()` read during render only
 * updates when something else happens to re-render. This makes that transition
 * happen on its own, without a reload.
 *
 * It also re-syncs when the tab becomes visible again: a laptop that slept
 * through the afternoon must reclassify immediately on wake, not up to one
 * interval later.
 */
export function useNow(intervalMs: number = DEFAULT_INTERVAL_MS): Date {
  const [now, setNow] = useState(() => new Date());

  useEffect(() => {
    const tick = () => setNow(new Date());
    const timer = window.setInterval(tick, intervalMs);
    const onVisible = () => {
      if (document.visibilityState === "visible") tick();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", tick);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", tick);
    };
  }, [intervalMs]);

  return now;
}
